import { Avatar } from '@base-ui/react/avatar';
import styled, { css } from 'styled-components';
import { pxToRem } from 'modules/v2/foundation/spacing';
import { themeGet, themeGetColor } from 'modules/v2/foundation/theme';
const containerSize = {
    '2xl': 56,
    xl: 48,
    lg: 40,
    md: 32,
    sm: 24,
    xs: 20,
    xxs: 16,
};
const letterFontSize = {
    '2xl': 18,
    xl: 18,
    lg: 16,
    md: 14,
    sm: 12,
    xs: 10,
    xxs: 8,
};
const iconSize = {
    '2xl': 24,
    xl: 20,
    lg: 16,
    md: 16,
    sm: 12,
    xs: 12,
    xxs: 8,
};
const backgroundColor = {
    primary: themeGet('colors.secondary.50'),
    secondary: themeGet('colors.primary.50'),
    success: themeGet('colors.green.50'),
    danger: themeGet('colors.red.50'),
    warning: themeGet('colors.yellow.50'),
    info: themeGet('colors.blue.50'),
    assistant: themeGet('colors.assistant.50'),
    magenta: themeGet('colors.magenta.50'),
    cyan: themeGet('colors.cyan.50'),
    orange: themeGet('colors.orange.50'),
    brown: themeGet('colors.brown.50'),
    indigo: themeGet('colors.indigo.50'),
    light: themeGet('colors.background.50'),
    dark: themeGet('colors.gray.50'),
    ghost: themeGetColor('background.50', 0.32),
};
const backgroundColorSolid = {
    primary: themeGet('colors.secondary.500'),
    secondary: themeGet('colors.primary.500'),
    success: themeGet('colors.green.500'),
    danger: themeGet('colors.red.500'),
    warning: themeGet('colors.yellow.500'),
    info: themeGet('colors.blue.500'),
    assistant: themeGet('colors.assistant.500'),
    magenta: themeGet('colors.magenta.500'),
    cyan: themeGet('colors.cyan.500'),
    brown: themeGet('colors.brown.500'),
    indigo: themeGet('colors.indigo.500'),
    orange: themeGet('colors.orange.500'),
    light: themeGet('colors.background.50'),
    dark: themeGet('colors.gray.1000'),
    ghost: themeGetColor('background.50', 0.32),
};
const iconColor = {
    primary: themeGet('colors.secondary.500'),
    secondary: themeGet('colors.primary.600'),
    success: themeGet('colors.green.700'),
    danger: themeGet('colors.red.500'),
    warning: themeGet('colors.yellow.900'),
    info: themeGet('colors.blue.600'),
    assistant: themeGet('colors.assistant.500'),
    magenta: themeGet('colors.magenta.500'),
    cyan: themeGet('colors.cyan.500'),
    brown: themeGet('colors.brown.500'),
    indigo: themeGet('colors.indigo.500'),
    orange: themeGet('colors.orange.500'),
    light: themeGet('colors.text.200'),
    dark: themeGet('colors.gray.0'),
    ghost: themeGet('colors.text.light'),
};
const borderColor = {
    primary: themeGet('colors.secondary.100'),
    secondary: themeGet('colors.primary.100'),
    success: themeGet('colors.green.100'),
    danger: themeGet('colors.red.100'),
    warning: themeGet('colors.yellow.100'),
    info: themeGet('colors.blue.100'),
    assistant: themeGet('colors.assistant.100'),
    magenta: themeGet('colors.magenta.100'),
    cyan: themeGet('colors.cyan.100'),
    brown: themeGet('colors.brown.100'),
    indigo: themeGet('colors.indigo.100'),
    orange: themeGet('colors.orange.100'),
    light: themeGet('colors.border.100'),
    dark: themeGet('colors.border.100'),
    ghost: themeGet('colors.text.light'),
};
const borderRadius = {
    circle: themeGet('borderRadius.circle'),
    square: themeGet('borderRadius.md'),
};
const solidBorderStyles = css `
  outline: ${({ $appearance }) => {
    const shadowColor = $appearance === 'light' ? borderColor.light : 'transparent';
    return css `
      ${pxToRem(1)} solid ${shadowColor}
    `;
}};

  outline-offset: -${pxToRem(1)};
`;
const borderStyles = css `
  border: ${(props) => css `
    ${pxToRem(1)} solid ${borderColor[props.$appearance]}
  `};
`;
const basicColorStyles = css `
  color: ${(props) => {
    if (props.$appearance === 'light' && props.$solid)
        return themeGet('colors.text.200');
    if (props.$appearance === 'dark' && !props.$solid)
        return themeGet('colors.gray.1000');
    return props.$solid ? themeGet('colors.gray.0') : iconColor[props.$appearance];
}};
`;
const getBorderStyles = (props) => (props.$solid ? solidBorderStyles : borderStyles);
export const Fallback = styled(Avatar.Fallback) `
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: ${themeGet('fontWeight.medium')};

  line-height: 1;
`;
export const Root = styled(Avatar.Root) `
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  overflow: hidden;
  user-select: none;
  border-radius: 100%;

  width: ${(props) => pxToRem(containerSize[props.$size])};
  height: ${(props) => pxToRem(containerSize[props.$size])};

  background-color: ${(props) => props.$solid ? backgroundColorSolid[props.$appearance] : backgroundColor[props.$appearance]};

  ${getBorderStyles};
  border-radius: ${(props) => borderRadius[props.$shape]};

  [data-icon='falcon'] {
    ${basicColorStyles};
    width: ${(props) => pxToRem(iconSize[props.$size])};
    height: ${(props) => pxToRem(iconSize[props.$size])};
  }

  ${Fallback} {
    font-size: ${(props) => pxToRem(letterFontSize[props.$size])};
    text-transform: uppercase;
    ${basicColorStyles}
  }
`;
